import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useT } from "../../../i18n";
import { usePico } from "../hooks/usePico";
import { track } from "../../../lib/telemetry/telemetry";
import { Button } from "../../../components/ui/Button";
import { ReadAloudButton } from "../../../components/ReadAloud";

/**
 * 휴식 안내(FS-C1-06 모달 · SR-SEC-05) — 교사 호스트 없는 비동기 놀이터(Phase 1)에서 **연속으로 논 시간** 30분마다
 * '🍵 30분 동안 놀았어. 잠깐 쉬고 다시 만나!' + '✓ 쉬고 왔어'. 확인하면 계측 break-ack(확인 이벤트 기록)을 남긴다.
 *  - 노는 시간 = 놀이 화면(코스 · 전시관 · 친구 기계 같이 놀기 · 리플레이)에 있고 탭이 보이는 동안만 센다.
 *  - 놀이 화면을 떠나 {@link REST_GAP_MS} 넘게 쉬면(다른 화면 · 탭 가림) '연속'이 끊겨 다시 0부터.
 *  - 교실 룸(PH2 — 교사 호스트)에서는 끈다(inRoom).
 *  - 문구는 피코 반응 카탈로그(break) → 셸 문구. SE 읽어 주기.
 * 센 시간은 탭 세션 동안 기억한다(새로 고침해도 다시 0이 되지 않게).
 */
export const BREAK_MS = 30 * 60_000;
export const REST_GAP_MS = 5 * 60_000;
const KEY = "pf.play";
const PLAY_ROUTES = [/^\/course/, /^\/gallery/, /^\/replay\//];

interface PlayClock {
  /** 연속으로 논 시간(ms) */
  playedMs: number;
  /** 마지막으로 논 시각(ms) — 이보다 REST_GAP_MS 넘게 지나면 끊김 */
  lastAt: number;
}

export const isPlayRoute = (path: string) => PLAY_ROUTES.some((r) => r.test(path));

/** 순수: 시계에 dt 를 더한다(놀고 있지 않으면 쉼으로 본다) */
export function advanceClock(c: PlayClock, now: number, playing: boolean, dtMs: number): PlayClock {
  if (!playing) return c;
  const gap = c.lastAt ? now - c.lastAt - dtMs : 0;
  const base = gap > REST_GAP_MS ? 0 : c.playedMs;
  return { playedMs: base + dtMs, lastAt: now };
}

function read(): PlayClock {
  try {
    const v = JSON.parse(window.sessionStorage.getItem(KEY) ?? "null") as PlayClock | null;
    if (v && Number.isFinite(v.playedMs) && Number.isFinite(v.lastAt)) return v;
  } catch {
    /* 저장소 없음 */
  }
  return { playedMs: 0, lastAt: 0 };
}

function write(c: PlayClock) {
  try {
    window.sessionStorage.setItem(KEY, JSON.stringify(c));
  } catch {
    /* 무시 */
  }
}

const TICK_MS = 1000;

export function BreakReminder({
  afterMs = BREAK_MS,
  inRoom = false,
}: {
  afterMs?: number;
  /** 교실 룸(PH2) — 휴식 안내 끔(SR-SEC-05) */
  inRoom?: boolean;
}) {
  const t = useT();
  const pico = usePico();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const btn = useRef<HTMLButtonElement>(null);
  const clock = useRef<PlayClock>(read());
  const reacted = useRef(false);
  const playing = !inRoom && isPlayRoute(pathname);

  // 1초마다: 놀이 화면 + 탭 보임이면 시간을 더한다(짧은 afterMs — 가짜 모드 ?break= — 는 더 촘촘히)
  useEffect(() => {
    if (!playing || open) return;
    const step = Math.max(50, Math.min(TICK_MS, Math.floor(afterMs / 4)));
    const h = window.setInterval(() => {
      if (document.visibilityState === "hidden") return;
      clock.current = advanceClock(read(), Date.now(), true, step);
      write(clock.current);
      if (clock.current.playedMs >= afterMs) setOpen(true);
    }, step);
    return () => window.clearInterval(h);
  }, [playing, open, afterMs]);

  // 모달 — 열리면 버튼으로 포커스를 옮기고, 닫힐 때까지 포커스가 밖으로 나가지 않게 붙잡는다(aria-modal)
  useEffect(() => {
    if (!open) return;
    btn.current?.focus();
    const keep = (e: FocusEvent) => {
      const dlg = btn.current?.closest("[role='dialog']");
      if (dlg && e.target instanceof Node && !dlg.contains(e.target)) btn.current?.focus();
    };
    document.addEventListener("focusin", keep);
    return () => document.removeEventListener("focusin", keep);
  }, [open]);

  // 열릴 때 피코 반응(break) 한 번 — 계측 pico
  useEffect(() => {
    if (!open || reacted.current) return;
    reacted.current = true;
    pico.react("break", { toast: false });
  }, [open, pico]);

  if (!open || inRoom) return null;
  const reaction = pico.resolve("break");
  const text = reaction.shownText || t("student.break.text");
  return (
    <div className="pf-modal" data-testid="break-reminder">
      <div
        className="pf-pane pf-modal__pane"
        role="dialog"
        aria-modal="true"
        aria-labelledby="break-text"
      >
        <div
          className="pf-banner pf-banner--ok"
          data-read-aloud=""
          data-pico-event="break"
          data-pico-source={reaction.source}
        >
          <div className="pf-banner__body">
            <span className="pf-banner__icon" aria-hidden="true">
              {reaction.avatar}
            </span>
            <p className="pf-banner__text" id="break-text">
              <b className="pf-banner__cause">{text}</b>
            </p>
            <ReadAloudButton text={text} />
          </div>
          <div className="pf-banner__actions">
            <Button
              ref={btn}
              variant="ghost"
              icon="✓"
              data-testid="break-ack"
              onClick={() => {
                // 확인 이벤트 기록(SR-SEC-05) — 몇 분 놀았는지(분)만
                track({
                  t: "break-ack",
                  code: "break",
                  value: Math.round(clock.current.playedMs / 60_000),
                });
                clock.current = { playedMs: 0, lastAt: Date.now() };
                write(clock.current);
                reacted.current = false;
                setOpen(false);
              }}
            >
              {t("student.break.done")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
